/* eslint-disable @typescript-eslint/camelcase */
import { ErrorCode } from "@/constant";
import { AuthType, Network } from "@/network";
import { Cookie, Log } from "@/util";

// ------------------------------ type ------------------------------

export type UserInfo = {
  name: string;
  account: string;
  mail: string;
  sales: string;
};

export type Moodboard = {
  id: string;
  materialSrcList: string[];
  title: string;
  description: string;
};

export type InfoMaterials = {
  id: string;
  uri: string;
  imgSrc: string;
  title: string;
};

export type CopyBoard = {
  id: string;
  title: string;
  selected: boolean;
};

export type MoveBoard = {
  selected: string;
  list: {
    id: string;
    title: string;
  }[];
};

export type MemberColorMapType = {
  [mcidKey: string]: {
    mcid: string;
    listName: string;
    colorMap: {
      [mccidKey: string]: {
        mccid: string;
        colorName: string;
        color: string;
        color_h: string;
        color_s: string;
        color_l: string;
      }
    }
  }
};

export type MemberColorType = {
  color?: string[];
  colorName: string;
  main: string;
  mcid: string;
  note: string;
};

export type MemberColorInfoType = {
  mcid: string;
  name: string;
  note: string;
  is_main: string;
  note_show: string;
};

export type MemberColorArrType = {
  color: string;
  mccid: string;
  name: string;
  hsl: string;
  hex: string;
};

export type MemberDesignType = {
  pic?: string[];
  mdlid: string;
  name: string;
  description: string;
  description_show?: string;
};

export type MemberDesignContType = {
  mdcid: string;
  maid: string;
  name: string;
  description: string;
  pic: string;
  cont?: string;
  gmaid: string;
  materialName?: string;
  active?: string;
};

// ------------------------------ class ------------------------------

class InfoModel {
  private static sInstance: InfoModel;

  public static get instance(): InfoModel {
    if (!InfoModel.sInstance) {
      InfoModel.sInstance = new InfoModel();
    }

    return InfoModel.sInstance;
  }

  private verificationCodeImgSrc = "";

  // ------------------------------ index ------------------------------

  private userInfo: UserInfo = {
    name: "",
    account: "",
    mail: "",
    sales: ""
  };
  private moodboardList: Moodboard[] = [];

  // ------------------------------ moodboard selected ------------------------------

  private materialsList!: InfoMaterials[];
  private copyBoardList: CopyBoard[] = [];
  private moveBoard!: MoveBoard;
  private name = '';
  private note = '';
  private note_show = '';

  public async refreshVerificationCodeImgSrc(item: object): Promise<void> {
    await Network.getImageSrcByPost("/captcha/getCode", item)
      .then(src => {
        this.verificationCodeImgSrc = src;
      })
      .catch(error => {
        Log.error(error);
      });
  }

  public async verificationCodeCheck(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/captcha/checkCode", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public async userLogin(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/login/check", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;

        if (errNo === ErrorCode.Success) {
          Cookie.setCookie("token", data.Authorization as string, "/", 1);
          Cookie.setCookie("member_priv", data.memberPriv as string, "/", 1);
          Cookie.setCookie("member_identity", data.memberIdentity as string, "/", 1);
          Network.setAuth(AuthType.Base, data.Authorization as string);
        }
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public recordUserStayTime(): void {
    Network.sendAPI("/member/updateLoginLog", "post");
  }

  public async editPassword(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/member/resetMemberPwd", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public async getUserBoardData(): Promise<void> {
    await Network.sendAPI("/member/getMemberInfo", "post")
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        this.convertInfoData(data);
      })
      .catch(error => {
        Log.error(error);
      });
  }

  public async getUserMaterialsData(item?: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/member/getCollectMaterialInfo", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
        console.log(data);
        this.convertMoodboardSelectedData(data);
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public async getUserFavoriteMaterialsData(): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/member/getFavoriteInfo", "post")
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
        this.convertFavoriteMoodboardSelectedData(data);
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public async createBoard(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/member/addMemberCollect", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public async editBoard(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/member/editMemberCollect", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public async deleteBoard(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/member/delMemberCollect", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public async addToBoard(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/member/addCollectMaterial", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public async copyToBoard(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/member/copyCollectMaterial", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public async switchFromFavoriteBoard(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/member/switchFavorite", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public async moveToSpecificBoard(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/member/moveCollectMaterial", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public async submitEmail(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/member/shareMaterial", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public async deleteMaterial(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/member/removeCollectMaterial", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public getVerificationCodeImgSrc(): string {
    return this.verificationCodeImgSrc;
  }

  // ------------------------------ index ------------------------------

  private convertInfoData(rawData: { [key: string]: unknown }): void {
    const memberInfo = rawData.memberInfo as {
      name: string;
      acc: string;
      mail: string;
    };
    const salesInfo = rawData.salesInfo as { mail: string; name: string };
    this.userInfo = {
      name: memberInfo.name,
      account: memberInfo.acc,
      mail: memberInfo.mail,
      sales: salesInfo.name + "(" + salesInfo.mail + ")"
    };

    const favoriteInfo = rawData.favoriteInfo as {
      count: number;
      pic: string[];
    };
    const memberCollectList = rawData.memberCollectList as {
      [key: string]: {
        name: string;
        note: string;
        picList: string[];
      };
    };
    const favoritePicList: string[] = [];
    favoriteInfo.pic.forEach((pic, index) => {
      if (index < 4) {
        favoritePicList.push(pic);
      }
    });
    this.moodboardList = [];
    const item = {
      id: "favorite",
      materialSrcList: favoritePicList,
      title: "My Favorite",
      description: ""
    };
    this.moodboardList.push(item);

    Object.keys(memberCollectList).forEach(key => {
      const raw = memberCollectList[key];
      const picList: string[] = [];
      raw.picList.forEach((pic, index) => {
        if (index < 4) {
          picList.push(pic);
        }
      });
      const item = {
        id: key,
        materialSrcList: picList,
        title: raw.name,
        description: raw.note
      };
      this.moodboardList.push(item);
    });
  }

  public getUserInfo(): UserInfo {
    return this.userInfo;
  }

  public getMoodboardList(): Moodboard[] {
    return this.moodboardList;
  }

  // ------------------------------ moodboard selected ------------------------------

  private convertMoodboardSelectedData(rawData: {
    [key: string]: unknown;
  }): void {
    const memberCollect = rawData.memberCollect as { name: string, note: string, note_show: string };
    if (memberCollect) {
      this.name = memberCollect.name as string;
      this.note_show = memberCollect.note_show as string;
      this.note = memberCollect.note as string;
    }

    this.materialsList = [];
    this.copyBoardList = [];
    this.moveBoard = {
      selected: "",
      list: []
    };

    const materialList = rawData.materialList as {
      link: string;
      maid: string;
      name: string;
      pic: { [key: string]: string };
      description: string;
    }[];
    materialList.forEach(material => {
      const item = {
        id: material.maid,
        uri: material.link,
        imgSrc: material.pic["01"],
        title: material.name,
        description: material.description
      };
      this.materialsList.push(item);
    });

    const memberCollectList = rawData.memberCollectList as {
      mcid: string;
      mid: string;
      name: string;
      note: string;
    }[];
    memberCollectList.forEach(memberCollect => {
      const copyBoardItem = {
        id: memberCollect.mcid,
        title: memberCollect.name,
        selected: false
      };
      this.copyBoardList.push(copyBoardItem);

      const moveBoardItem = {
        id: memberCollect.mcid,
        title: memberCollect.name
      };
      this.moveBoard.list.push(moveBoardItem);
    });
  }

  private convertFavoriteMoodboardSelectedData(rawData: {
    [key: string]: unknown;
  }): void {
    this.materialsList = [];
    this.copyBoardList = [];
    this.moveBoard = {
      selected: "",
      list: []
    };

    const materialList = rawData.favoriteList as {
      link: string;
      maid: string;
      name: string;
      pic: { [key: string]: string };
      description: string;
    }[];
    materialList.forEach(material => {
      const item = {
        id: material.maid,
        uri: material.link,
        imgSrc: material.pic["01"],
        title: material.name,
        description: material.description,
      };
      this.materialsList.push(item);
    });

    const memberCollectList = rawData.memberCollectList as {
      mcid: string;
      mid: string;
      name: string;
      note: string;
    }[];
    memberCollectList.forEach(memberCollect => {
      const copyBoardItem = {
        id: memberCollect.mcid,
        title: memberCollect.name,
        selected: false
      };
      this.copyBoardList.push(copyBoardItem);

      const moveBoardItem = {
        id: memberCollect.mcid,
        title: memberCollect.name
      };
      this.moveBoard.list.push(moveBoardItem);
    });
  }

  public getMaterialsList(): InfoMaterials[] {
    return this.materialsList;
  }

  public getCopyBoardList(): CopyBoard[] {
    return this.copyBoardList;
  }

  public getMoveBoard(): MoveBoard {
    return this.moveBoard;
  }

  public getBoardName(): string {
    return this.name;
  }

  public getBoardNote(): string {
    return this.note;
  }

  public getBoardNoteShow(): string {
    return this.note_show;
  }

  // 取得會員的調色盤清單
  public async getMemberColorMap(): Promise<[number, MemberColorMapType, string[]]> {
    let errNo = ErrorCode.NotResponse;
    let memberColorMap: MemberColorMapType = {};
    let orderArr: string[] = [];

    await Network.sendAPI("/member/getMemberColorMap", "post")
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
        if (errNo === ErrorCode.Success) {
          memberColorMap = data.map as MemberColorMapType;
          orderArr = data.orderArr as string[];
        }
      })
      .catch(error => {
        Log.error(error);
      });

    return [errNo, memberColorMap, orderArr];
  }

  // palette (color)
  public async getUserColorBoardData(): Promise<void> {
    await Network.sendAPI("/member/getMemberInfo", "post")
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        this.convertInfoData(data);
      })
      .catch(error => {
        Log.error(error);
      });
  }
  
  // fbx (admin)
  public async addModelProFbx(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/admin/model_pro/addModelProFbx", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });
    
    return errNo;
  }
  

  // Palette調色盤
  public async createPaletteBoard(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/member/addMemberColor", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public async getPaletteBoardList(): Promise<MemberColorType[]> {
    let memberColorList: MemberColorType[] = [];
    await Network.sendAPI("/member/getMemberColorList", "post")
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        console.log(data);
        memberColorList = data.memberColorList as MemberColorType[];
      })
      .catch(error => {
        Log.error(error);
      });

    return memberColorList;
  }

  public async editPaletteBoard(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    console.log(item);
    await Network.sendAPI("/member/editMemberColor", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public async deletePaletteBoard(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/member/delMemberColor", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public async getPaletteList(item?: object): Promise<[MemberColorInfoType, MemberColorArrType[]]> {
    let memberColor: MemberColorInfoType = {
      mcid: '',
      name: '',
      note: '',
      is_main: '0',
      note_show: '',
    };
    let memberColorArr: MemberColorArrType[] = [];

    await Network.sendAPI("/member/getMemberColorCont", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        memberColor = data.memberColor as MemberColorInfoType;
        memberColorArr = data.memberColorArr as MemberColorArrType[];
        console.log(data);
      })
      .catch(error => {
        Log.error(error);
      });

    return [memberColor, memberColorArr];
  }  

  public async addPalette(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/member/addMemberColorCont", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public async deletePalette(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/member/delMemberColorCont", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public async editPaletteColorCont(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/member/editMemberColorCont", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public async getMemberDesignFrontList(): Promise<MemberDesignType[]> {
    let memberDesignList: MemberDesignType[] = [];
    await Network.sendAPI("/member/getMemberDesignFrontList", "post")
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        memberDesignList = data.memberDesignList as MemberDesignType[];
      })
      .catch(error => {
        Log.error(error);
      });

    return memberDesignList;
  }

  // 建立 Design 設計本清單
  public async createDesignBoard(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/member/addMemberDesignList", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  // 編輯 Design 設計本清單
  public async editDesignBoard(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/member/editMemberDesignList", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  // 刪除 Design 設計本清單
  public async deleteDesignBoard(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/member/delMemberDesignList", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  // 取得 DesignCont 設計本內容列表
  public async getDesignList(item?: object): Promise<[MemberDesignType, MemberDesignContType[]]> {
    let memberDesign: MemberDesignType = {
      mdlid: '',
      name: '',
      description: '',
    };
    let memberDesignArr: MemberDesignContType[] = [];

    await Network.sendAPI("/member/getMemberDesignCont", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        memberDesign = data.memberDesign as MemberDesignType;
        memberDesignArr = data.memberDesignArr as MemberDesignContType[];
      })
      .catch(error => {
        Log.error(error);
      });

    return [memberDesign, memberDesignArr];
  }  
  
  // 編輯 DesignCont 設計本內容
  public async editMemberDesignCont(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/member/editMemberDesignCont", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  // 刪除 DesignCont 設計本內容
  public async deleteDesign(item: object): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.sendAPI("/member/delMemberDesignCont", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public async getMemberDesignContInfo(item: object): Promise<[number, MemberDesignContType]> {
    let errNo = ErrorCode.NotResponse;
    let memberDesignCont = {} as any;
    await Network.sendAPI("/materials/getMemberDesignContInfo", "post", item)
      .then(response => {
        const rawData = response.data as { [key: string]: unknown };
        console.log(rawData);

        errNo = rawData.ERR_CODE as number;
        if (errNo == ErrorCode.Success) {
          memberDesignCont = rawData.memberDesignCont as MemberDesignContType;
        }
      })
      .catch(error => {
        Log.error(error);
      });

    return [errNo, memberDesignCont];
  }
}

const infoModel = InfoModel.instance;

export { infoModel as InfoModel };
